<!--
[TASK-148] 미결제 과금건 일괄 재결제 처리 (작업 모달)
 - 선례 : bank-deposit-request/cancel.vue + 공용 mixin workable-by-one.vue
 - 선택한 과금건마다 기존 단건 재결제 API (POST /billing-retry/{id}) 를 override 파라미터 없이 순차 호출한다.
   (= 사람이 단건 재결제를 반복해서 누르는 것과 동일. 결제수단은 서버가 소유 회원사의 "현재 기본 결제수단" 으로 결정)
 - 실패 건이 나와도 멈추거나 되돌리지 않고 다음 건 진행, 건별 성공/실패 + 실패 사유를 표에 표시한다. (선례 동작 그대로)
------------------------------------
-->
<template>
    <!-- 모달 -->
    <div class="uk-modal-full" uk-modal esc-close="false" bg-close="false">
        <div class="uk-modal-dialog">
            <button class="uk-modal-close-full uk-close-large" type="button" uk-close></button>
            <div uk-grid class="uk-grid-small uk-padding" uk-height-viewport>


                <div class="uk-card uk-card-default uk-card-body uk-card-small uk-width-large">
                    <!-- 대기화면 -->
                    <div v-show="jobState === 'ready'">
                        <p class="uk-text-center uk-text-large">
                            선택한 미결제 과금건 {{ targetCount }}건을<br />
                            재결제 합니다.
                        </p>
                        <p class="uk-text-center">
                            선택 합계 <strong class="uk-text-primary">{{ totalAmountText }}</strong> 원
                        </p>

                        <!-- 결제수단 안내 (결제수단 선택 UI 는 없음 — 현재 설정된 기본 결제수단으로 진행) -->
                        <div class="billing-retry-guide">
                            <p>
                                선택한 과금건은 <strong>현재 설정된 기본 결제수단</strong>으로 결제됩니다.
                                다른 결제수단으로 결제하려면
                                <a v-if="settingUrl" :href="settingUrl" target="_blank" rel="noopener">[결제수단 관리]</a>
                                <span v-else>[결제수단 관리]</span>
                                에서 기본 결제수단을 먼저 변경한 뒤 진행하세요.
                            </p>
                            <p>물품가비용은 기본 결제수단과 관계없이 예치금으로 결제됩니다.</p>
                            <p>카드로 결제되는 건은 카드 수수료가 더해져 결제금액이 달라질 수 있습니다.</p>
                        </div>

                        <div class="uk-text-center">
                            <button
                                class="uk-button uk-button-primary uk-button-large billing-retry-start"
                                type="button"
                                v-on:click="jobStart()"
                            >
                                <span uk-icon="icon: play"></span>
                                재결제 시작
                            </button>
                        </div>


                    </div>


                    <!-- 실행중 -->
                    <div v-show="jobState === 'doing'">
                        <p class="uk-text-center uk-text-danger uk-text-large">
                            작업이 진행중입니다.<br />
                            작업이 종료될때까지 브라우저를 닫지마세요
                        </p>
                    </div>


                    <!-- 완료 -->
                    <div v-show="jobState === 'done'">
                        <p class="uk-text-center uk-text-primary uk-text-large">
                            작업이 완료되었습니다.<br />
                            브라우저를 닫아도 좋습니다.
                        </p>

                        <div class="uk-text-center">
                            <button
                                class="uk-button uk-button-primary uk-button-large"
                                type="button"
                                v-on:click="refreshPage()"
                            >
                                <span uk-icon="icon: refresh"></span>
                                현재 페이지 새로고침
                            </button>
                        </div>
                    </div>

                </div>



                <div class="uk-width-expand uk-margin-top app-table-normal">
                    <!-- 진행률 -->
                    <div class="uk-text-large uk-text-primary uk-margin-left">
                        {{ successCount }} / {{ targetCount }}
                        <span v-show="failCount > 0" class="uk-text-danger">(실패 : {{ failCount }})</span>
                    </div>
                    <progress id="js-progressbar" class="uk-progress uk-margin-remove" :value="successCount" :max="targetCount" ></progress>


                    <!-- 리스트 -->
                    <table class="uk-table uk-table-small uk-table-middle app-prod-table uk-margin-top">
                        <colgroup>
                            <col style="width: 8rem">
                            <col style="width: 6rem">
                            <col v-if="showUserNick" style="min-width: 10rem">
                            <col style="width: 8rem">
                            <col style="width: 8rem">
                            <col style="min-width: 12rem">
                            <col style="width: 9rem">
                            <col style="min-width: 12rem">
                        </colgroup>

                        <thead>
                        <tr>
                            <th scope="col" class="uk-text-center">
                                진행상태
                            </th>
                            <th scope="col" class="uk-text-center">
                                과금ID
                            </th>
                            <th v-if="showUserNick" scope="col" class="uk-text-center">
                                회원사
                            </th>
                            <th scope="col" class="uk-text-center">
                                과금타입
                            </th>
                            <th scope="col" class="uk-text-center">
                                과금기준일
                            </th>
                            <th scope="col" class="uk-text-center">
                                적용 결제수단
                            </th>
                            <th scope="col" class="uk-text-center">
                                결제금액
                            </th>
                            <th scope="col" class="uk-text-center">
                                기존 실패 사유
                            </th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr
                            v-for="target in jobTargets"
                            :key="target.item.id"
                        >
                            <td class="uk-text-center">
                                <div v-show="target.state === 'ready'" class="uk-text-muted">
                                    대기
                                </div>
                                <div v-show="target.state === 'doing'" class="uk-text-primary">
                                    <span uk-icon="icon: play"></span>
                                    처리중..
                                </div>
                                <div v-show="target.state === 'success'" class="uk-text-success">
                                    <span uk-icon="icon: check"></span>
                                    성공
                                </div>
                                <div v-show="target.state === 'fail'" class="uk-text-danger uk-position-relative">
                                    <span uk-icon="icon: close"></span>
                                    실패
                                    <!-- 작업메세지 -->
                                    <div v-show="target.job_msg" class="uk-alert-danger uk-position-top-left w-30rem m-0 ml-5rem uk-text-left" uk-alert>
                                        <a href class="uk-alert-close" uk-close></a>
                                        {{ target.job_msg }}
                                    </div>
                                </div>
                            </td>

                            <td class="uk-text-center">
                                {{ target.item.id }}
                            </td>
                            <td v-if="showUserNick" class="uk-text-center">
                                {{ target.item.user_nick }}
                            </td>
                            <td class="uk-text-center">
                                {{ target.item.billing_type_text }}
                            </td>
                            <td class="uk-text-center">
                                {{ target.item.billing_date }}
                            </td>
                            <td class="uk-text-center">
                                {{ target.item.apply_method_label }}
                            </td>
                            <td class="uk-text-center">
                                {{ Number(target.item.total_amount).toLocaleString() }} 원
                            </td>
                            <td class="uk-text-center">
                                {{ target.item.arrears_reason }}
                            </td>
                        </tr>

                        </tbody>
                    </table>

                </div>
            </div>
        </div>
    </div>


</template>

<script>
    import workableByOne from '../workable-by-one';

    export default {
        mixins: [
            workableByOne,
        ],
        props: {
            // csrf-token
            csrfToken: String,

            // 1회 처리 상한 건수 (클라이언트 가드)
            retryLimit: {
                type: Number,
                default: 50
            },

            // 회원사 열 표시 여부 (총괄 목록만 true)
            showUserNick: {
                type: Boolean,
                default: false
            },

            // 결제수단 관리 화면 링크 (회원사 화면만 전달)
            settingUrl: {
                type: String,
                default: null
            },
        },
        data: function () {
            return {
            };
        },
        computed: {
            // 선택 합계 금액
            totalAmount() {
                return _.sumBy(this.jobTargets, (target) => Number(target.item.total_amount) || 0);
            },

            // 선택 합계 금액 (표시용)
            totalAmountText() {
                return this.totalAmount.toLocaleString();
            },
        },
        methods: {
            /**
             * 작업초기화 완료 후 모달창 띄우기 바로직전에 호출됨.
             * 처리 순서 = 과금ID 오름차순 (오래된 건 먼저 — 예치금이 모자라면 먼저 처리된 건만 결제됨)
             */
            jobInitialized() {
                let sorted = _.sortBy(this.jobTargets, (target) => Number(target.item.id));
                for (let i = 0; i < sorted.length; i++) {
                    sorted[i].next = i + 1 < sorted.length ? sorted[i + 1] : null;
                    sorted[i].prev = i > 0 ? sorted[i - 1] : null;
                }
                this.jobTargets = sorted;
            },

            /**
             * (실제 사용처에서 재정의)
             *
             * 작업시작 준비단계
             * - 값 검증 (상한 건수)
             * - 컨펌 처리
             * @return boolean
             */
            prepareJobStart() {
                // 상한 초과 차단
                if (this.targetCount > this.retryLimit) {
                    alert('한 번에 최대 ' + this.retryLimit + '건까지 재결제할 수 있습니다. (현재 ' + this.targetCount + '건 선택) 선택 건수를 줄여 주세요.');
                    return false;
                }

                // 컨펌
                return confirm(
                    '선택한 ' + this.targetCount + '건 (합계 ' + this.totalAmountText + '원)을 현재 기본 결제수단으로 재결제합니다. 실행합니까?'
                );
            },

            /**
             * 재결제 응답으로 행 표시 갱신 (적용된 결제수단 / 최종 결제금액)
             *  - 응답은 실제 서버 처리 결과이므로, 안내용으로 표시했던 적용 결제수단과 다르면 응답값이 우선한다.
             * @param target 작업대상
             * @param response 재결제 API 응답
             */
            applyResult(target, response) {
                if (response.total_amount !== undefined && response.total_amount !== null) {
                    target.item.total_amount = Number(response.total_amount);
                }

                let type = Number(response.payment_method_type);
                let current = target.item.apply_method_label || '';
                if (type === 1) {
                    // 예치금 (물품가 전용 표기는 유지)
                    target.item.apply_method_label = current.indexOf('예치금') === 0 ? current : '예치금';
                } else if (type === 2) {
                    // 카드 (카드명 표기는 유지)
                    target.item.apply_method_label = current.indexOf('카드') === 0 ? current : '카드';
                }
            },

            /**
             * getWorkPromise(target)
             * 1개 작업을 실행하는 Promise를 반환함.
             *  - resolve(job_msg) {}
             *  - reject(job_msg) {}
             *
             * 재결제 API 는 결제 실패도 HTTP 200 + success:false 로 돌려주므로 응답의 success 값을 보고 판정한다.
             *  - success === true  : 성공
             *  - success === false : 실패 (사유 = 평문 arrears_reason)
             *  - HTTP 오류 (422 등) : ajaxWorkPromise 가 응답 본문의 msg 로 reject
             *
             * @param target 작업대상 (jobTargets 배열의 아이템)
             * @return Promise
             */
            getWorkPromise(target) {
                target.state = 'doing';

                return this.ajaxWorkPromise({
                    type: 'POST',
                    url: '/billing-retry/' + target.item.id,
                    // override_payment_method_type 을 보내지 않는다 (서버가 현재 기본 결제수단으로 결정)
                    data: {
                        _token: this.csrfToken,
                    },
                    dataType: 'json',
                }).then((response) => {
                    this.applyResult(target, response);

                    if (response.success === true) {
                        return response.msg;
                    }
                    return Promise.reject(response.arrears_reason || '재결제에 실패하였습니다.');
                });
            },
        }
    }
</script>

<style lang="scss" scoped>
.billing-retry-guide {
    margin: 1rem 0;
    padding: 0.5rem 1rem;
    background: #f8f8f8;
    border: 1px solid #e5e5e5;
    border-radius: 4px;
    font-size: 1rem;

    p {
        margin: 0.5rem 0;
    }
}
</style>
